import type { AnalysisResult } from '../lib/types';
import { TermList } from './TermList';
import { Badge } from './ui';
import { SparkIcon } from './ui/icons';

const BAND_LABEL: Record<AnalysisResult['band'], string> = {
  strong: 'Strong term coverage',
  partial: 'Partial term coverage',
  weak: 'Low term coverage',
};

const BAND_TONE: Record<AnalysisResult['band'], 'verified' | 'gap' | 'danger'> = {
  strong: 'verified',
  partial: 'gap',
  weak: 'danger',
};

const BAND_TEXT: Record<AnalysisResult['band'], string> = {
  strong: 'text-verified',
  partial: 'text-gap',
  weak: 'text-danger',
};

// Literal class names so Tailwind's content scanner keeps them.
const BAND_STROKE: Record<AnalysisResult['band'], string> = {
  strong: 'stroke-verified',
  partial: 'stroke-gap',
  weak: 'stroke-danger',
};

function CoverageRing({ score, band }: { score: number; band: AnalysisResult['band'] }) {
  // r=42 → circumference ≈ 263.9; offset shows `score`% of the ring.
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - score / 100);

  return (
    <div className="relative h-32 w-32 shrink-0" aria-hidden="true">
      <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          strokeWidth="9"
          className="stroke-line"
        />
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          strokeWidth="9"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className={`${BAND_STROKE[band]} transition-[stroke-dashoffset] duration-700 ease-out`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-3xl font-semibold leading-none ${BAND_TEXT[band]}`}>
          {score}
        </span>
        <span className="mt-1 text-[11px] font-medium uppercase tracking-wide text-ink-faint">
          of 100
        </span>
      </div>
    </div>
  );
}

export function ResultsPanel({ result }: { result: AnalysisResult }) {
  const { score, band, jobTermCount, matched, missing, suggestions } = result;
  const matchedCount = matched.length;
  const total = jobTermCount || 1;

  return (
    <div className="flex flex-col gap-5">
      <section
        aria-labelledby="score-heading"
        className="rounded-lg border border-line bg-surface p-5 shadow-card sm:p-6"
      >
        <div className="flex items-center justify-between gap-3">
          <h2
            id="score-heading"
            className="text-xs font-semibold uppercase tracking-[0.14em] text-ink-soft"
          >
            Match score
          </h2>
          <Badge tone={BAND_TONE[band]} dot>
            {BAND_LABEL[band]}
          </Badge>
        </div>

        <div className="mt-4 flex flex-col items-center gap-5 sm:flex-row sm:items-center sm:gap-7">
          <CoverageRing score={score} band={band} />

          <div className="min-w-0 flex-1">
            <p className="text-sm text-ink-soft">
              <span className="text-base font-semibold text-ink">
                {matchedCount} of {total}
              </span>{' '}
              key job terms appear in your resume.
            </p>

            <div
              className="mt-4 h-2.5 w-full overflow-hidden rounded-pill bg-line"
              role="progressbar"
              aria-valuenow={score}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`Match score ${score} out of 100`}
            >
              <div
                className={`h-full rounded-pill ${
                  band === 'strong' ? 'bg-verified' : band === 'partial' ? 'bg-gap' : 'bg-danger'
                }`}
                style={{ width: `${score}%` }}
              />
            </div>

            <p className="mt-4 text-sm text-ink-soft">
              This is a <strong className="font-semibold text-ink">keyword-coverage heuristic</strong>,
              not a prediction of any employer&apos;s real ATS decision.
            </p>
          </div>
        </div>
      </section>

      <div className="grid gap-5 sm:grid-cols-2">
        <TermList
          title="Matched terms"
          headingId="matched-heading"
          terms={matched}
          tone="matched"
          emptyLabel="No job terms matched yet. Try reflecting the role's language where it is true for you."
        />
        <TermList
          title="Missing terms"
          headingId="missing-heading"
          terms={missing}
          tone="missing"
          emptyLabel="Every key job term is already present in your resume."
        />
      </div>

      <section
        aria-labelledby="suggestions-heading"
        className="rounded-lg border border-accent/20 bg-accent-soft p-5 sm:p-6"
      >
        <div className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className="flex h-7 w-7 items-center justify-center rounded-pill bg-accent text-white"
          >
            <SparkIcon className="h-4 w-4" />
          </span>
          <h2
            id="suggestions-heading"
            className="text-xs font-semibold uppercase tracking-[0.14em] text-accent-strong"
          >
            How to improve
          </h2>
        </div>
        <ul className="mt-3 flex list-disc flex-col gap-2 pl-5 text-sm text-ink marker:text-accent">
          {suggestions.map((text, index) => (
            <li key={index} className="max-w-prose">
              {text}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
